import { ActionIcon, Avatar, Badge, Box, Button, Divider, Flex, Grid, Group, Modal, NumberInput, Paper, Select, Stack, Text, Textarea, TextInput } from "@mantine/core"
import { FloppyDiskIcon, MinusIcon, PencilSimpleIcon, PercentIcon, PlusIcon, ImageIcon, TrashIcon } from "@phosphor-icons/react";
import classes from "./dynamic-form.module.css"
import { FitTable } from "../order/fit-table"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { IAdditionalLine } from '../../models/AdditionalLine'
import { IAttachment } from '../../models/Attachment'
import { currencyFormat, lineDiscountAmount, lineNetAmount } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { useVariantPricing } from "../order/use-variant-pricing"
import { NumberCurrencyField } from "../form/number-currency-field"
import { ColumnType, Table } from "../table"
import RenderText from "../ui/render-text"
import { VariantSelectorDrawer } from "../shared/variant-selector-drawer"
import { SupplierSalesDynamicAdditionalForm } from "./dynamic-additional-form"
import { HelpIcon } from "@dehub/theme"

const PRICE_SRC_KEY: Record<string, string> = { PROMOTION: "promotion", SPECIAL: "special", SEGMENT: "segment", BASE: "base", FREE_GOODS: "gift" }

type Props = {
  values?: any
  setFieldValue?: any
  errors?: any
}

export function SupplierSalesDynamicForm({ values, setFieldValue, errors }: Props) {
  const now_date = new Date()
  const { t } = useTranslation(["supplierSales", "common"])
  const [edit, setEdit] = React.useState<string>("")
  const [action, setAction] = React.useState<string[]>([])
  const [reload, setReload] = React.useState<boolean>(false)
  const [drawerOpened, setDrawerOpened] = useState(false)

  // Status guard: only fetch/apply prices for draft orders (REGISTERED)
  // values.orderStatus is available because form.tsx spreads action[1] into form state
  const currentOrderStatus = values?.orderStatus
  const isEditable = !currentOrderStatus || currentOrderStatus === "REGISTERED"

  const priceLines = useMemo(
    () => (values?.lines || []).map((l: any) => ({ variantId: l.variantId, quantity: l.quantity || 1 })),
    [values?.lines],
  )

  // In supplier-sales: senderBusinessId = seller/supplier, receiverBusinessId = buyer
  const { priceByVariant } = useVariantPricing(
    isEditable ? values?.senderBusinessId : undefined,
    isEditable ? values?.receiverBusinessId : undefined,
    isEditable ? priceLines : [],
  )

  useEffect(() => {
    if (!isEditable) return
    ;(values?.lines || []).forEach((l: any, i: number) => {
      const fresh = priceByVariant[l.variantId]
      if (fresh != null && fresh !== l.price) {
        setFieldValue(`lines.${i}.price`, fresh)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceByVariant, isEditable])

  const columns = useHeader({
    t,
    reload,
    values,
    setFieldValue,
    errors,
    onClick: (key, item) => {
      if (key === "remove") {
        setReload(true)
        setFieldValue("lines", [...values.lines.filter((i: any, index: number) => item.index !== index)])
        setTimeout(() => {
          setReload(false)
        }, 400)
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  const columnsAdditional = useHeaderAdditional({
    t,
    edit,
    setEdit,
    setFieldValue,
    values,
    errors,
    onClick: (key, item) => {
      if (key === "remove") {
        setFieldValue("additionalLines", [...values.additionalLines.filter((i: any, index: number) => item.index !== index)])
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  const columnsAttachments = useHeaderAttachments({
    t,
    values,
    errors,
    setFieldValue,
    onClick: (key, item) => {
      if (key === "remove") {
        setReload(true)
        setFieldValue("attachments", [...values.attachments.filter((i: any, index: number) => item.index !== index)])
        setTimeout(() => {
          setReload(false)
        }, 200)
      } else if (key === "edit") {
        setAction(["edit", item])
      }
    },
  })

  const len = Object.keys(values?.lines || [])?.length

  // ── Order totals, on de-order-service's convention ────────────────────────
  // DSC-001: these were computed inline in the JSX below as
  // `Sum(qty*price) + Sum(qty*vatAmount) + Sum(qty*taxAmount) - lineDiscount + ...
  //  + shipping`, with the order-level percentage taken off THAT. Prices are
  // VAT-INCLUSIVE, so the VAT and city tax were being added on top of an amount
  // that already contained them, and the shipping fee sat inside the percentage
  // base. order/create.ts:553-556 builds ORDER_AMOUNT from lines plus additional
  // lines only, applies the discount to that (line 573), clamps it to the order
  // amount (line 583) and adds shipping afterwards (line 621).
  //
  // This form now routinely carries an order-level discount: B.8 auto-fills it
  // from the buyer's payment-term rule on the create form, and the order lands
  // here to be edited.
  const summaryLines: any[] = values?.lines || []
  const summaryAddLines: any[] = values?.additionalLines || []
  const lineSubtotal = summaryLines.reduce((s: number, i: any) => s + lineNetAmount(i), 0)
  const additionalSubtotal = summaryAddLines.reduce(
    (s: number, i: any) =>
      s +
      (i?.quantity || 0) * (i?.price || 0) -
      // An additional line's AMOUNT discount is flat, not per unit — the
      // convention this form has always used for that table.
      ((i?.discountType === "PERCENTAGE"
        ? ((i?.price || 0) * (i?.quantity || 0) * (i?.discountValue || 0)) / 100
        : i?.discountValue) || 0),
    0,
  )
  const orderAmount = lineSubtotal + additionalSubtotal
  const orderDiscountValue = Number(values?.discountValue) || 0
  const orderLevelDiscount = values?.discountType
    ? Math.min(
      values?.discountType === "PERCENTAGE" ? (orderAmount * orderDiscountValue) / 100 : orderDiscountValue,
      orderAmount,
    )
    : 0
  const grandTotal = orderAmount - orderLevelDiscount + (values?.shippingAmount || 0)
  const codAdvance = (grandTotal * (values?.paymentTerm?.advancePercent || 0)) / 100

  const additionalLines: any[] = values?.additionalLines || []
  const attachments: any[] = values?.attachments || []
  const isCod = values?.paymentTerm?.condition === "COD"

  const addAdditionalLine = () => {
    setFieldValue("additionalLines", [
      ...(values?.additionalLines || []),
      {
        name: "",
        unit: "",
        price: "",
        quantity: "",
        discountType: "",
        discountValue: "",
        isEdit: true,
        isNew: true,
      },
    ])
  }

  const addAttachment = () => {
    setFieldValue("attachments", [
      ...(values?.attachments || []),
      {
        name: "",
        url: "",
        description: "",
        isEdit: true,
        isNew: true,
      },
    ])
  }

  // «Захиалгын нэгтгэл» rows. Every figure is the expression the previous
  // summary grid rendered; only the layout changed (Ord_SalesEdit).
  const summaryRows: { label: string; value: string }[] = [
    { label: t("supplierSales:sales.orderTypesCount"), value: `${len} ${t("supplierSales:sales.types")}` },
    {
      label: t("supplierSales:sales.totalQuantity"),
      value: `${values?.lines?.reduce((total: number, item: any) => total + item!.quantity || 0, 0) || 0} ${t("supplierSales:sales.pieces")}`,
    },
    {
      label: t("supplierSales:sales.orderTotalAmount"),
      value: currencyFormat(values?.lines?.reduce((total: number, item: any) => total + item!.quantity * item?.price || 0, 0) || 0),
    },
    {
      label: t("supplierSales:sales.calculatedVat"),
      value: currencyFormat((values?.lines || [])?.reduce((total: number, item: any) => total + item!.quantity * item?.vatAmount || 0, 0) || 0),
    },
    {
      label: t("supplierSales:sales.calculatedTax"),
      value: currencyFormat((values?.lines || []).reduce((total: number, item: any) => total + item!.quantity * item?.taxAmount || 0, 0) || 0),
    },
    // Sum of the line discounts (this row showed the shipping fee before; НХАТ summed a non-existent `values.items`).
    { label: t("supplierSales:sales.calculatedDiscount"), value: currencyFormat(summaryLines.reduce((s: number, i: any) => s + lineDiscountAmount(i), 0)) },
    { label: t("supplierSales:sales.shippingFee"), value: currencyFormat(values?.shippingAmount || 0) },
    {
      label: `${t("supplierSales:sales.priceDiscount")}${values?.discountType === "PERCENTAGE" ? ` ${orderDiscountValue}% (${t("supplierSales:sales.percentage")})` : ""}`,
      value: orderLevelDiscount > 0 ? `-${currencyFormat(orderLevelDiscount)}` : currencyFormat(0),
    },
    { label: t("supplierSales:sales.additionalTotal"), value: currencyFormat(summaryAddLines.length ? additionalSubtotal : 0) },
  ]

  return (
    <Stack gap="md">
      {/* 1. Бараа бүтээгдэхүүн, ажил үйлчилгээ + Нэмэлтээр — one card */}
      <Paper className={classes.card} data-flush data-help-id="items-card">
        <Group justify="space-between" wrap="wrap" gap="sm" className={classes.cardHead}>
          <Text className={classes.cardTitle}>
            {t("supplierSales:sales.productsSection")} <Text span className={classes.count}>· {len}</Text><HelpIcon helpKey="order.supplier-sales-new.field.items" />
          </Text>
          <Group gap="sm">
            <Button variant="default" onClick={() => setDrawerOpened(true)} disabled={!isEditable}>
              {t("supplierSales:sales.addPackage")}
            </Button>
            <Button color="dark" leftSection={<PlusIcon size={16} />} onClick={() => setAction(["add", values])}>
              {t("supplierSales:sales.addProduct")}
            </Button>
          </Group>
        </Group>
        <FitTable columns={columns} rows={values?.lines || []} empty={t("supplierSales:sales.pleaseAddProduct")} />

        <Box className={classes.subSection}>
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Text className={classes.subTitle}>
              {t("supplierSales:sales.additionalSection")} <Text span className={classes.count}>· {additionalLines.length}</Text><HelpIcon helpKey="order.supplier-sales-new.field.additional" />
            </Text>
            <Button variant="default" leftSection={<PlusIcon size={16} />} onClick={addAdditionalLine}>
              {t("common:actions.add")}
            </Button>
          </Group>
          {additionalLines.length === 0 ? (
            <Text className={classes.muted} mt="sm">
              {t("supplierSales:sales.noAdditionalItems")}
            </Text>
          ) : (
            <Box className={classes.plainTable} mt="sm">
              <Table columns={columnsAdditional} name="values.additionalLines" dataSource={values.additionalLines} pagination={false} />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Mantine ignores `hidden`: render only on error. */}
      {errors?.items && (
        <Text fz={14} c="red.8" role="alert">
          {t("supplierSales:sales.pleaseAddProduct")}
        </Text>
      )}

      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {/* 2. Тэмдэглэл — the two notes, then the shipping fee and order discount inputs */}
            <Paper className={classes.card} p={20}>
              <Text className={classes.cardTitle} mb="sm">
                {t("supplierSales:sales.notes")}
              </Text>
              <Stack gap="sm">
                <Textarea
                  label={t("supplierSales:sales.customerAdditionalInfo")}
                  placeholder={t("supplierSales:sales.enterDescription")}
                  maxLength={255}
                  rows={2}
                  value={values?.senderNote ?? ""}
                  onChange={(e) => setFieldValue("senderNote", e.target.value)}
                />
                <Textarea
                  label={t("supplierSales:sales.additionalNote")}
                  placeholder={t("supplierSales:sales.enterNote")}
                  maxLength={255}
                  rows={2}
                  value={values?.senderAdditionalNote ?? ""}
                  onChange={(e) => setFieldValue("senderAdditionalNote", e.target.value)}
                />
                <Grid gap="sm">
                  <Grid.Col span={{ base: 12, sm: 5 }} data-help-id="fee">
                    <NumberCurrencyField
                      max={1000000000}
                      value={values.shippingAmount}
                      onChange={(v) => setFieldValue("shippingAmount", v)}
                      error={errors?.shippingAmount}
                      label={<>{t("supplierSales:sales.shippingFee")}<HelpIcon helpKey="order.supplier-sales-new.field.shipping-fee" /></>}
                      placeholder="0.00"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 7 }}>
                    <Text fz={14} fw={500} c="dark.6" mb={4}>
                      {t("supplierSales:sales.priceDiscount")}<HelpIcon helpKey="order.supplier-sales-new.field.term-discount" />
                    </Text>
                    <Flex gap={8}>
                      {values.discountType === "PERCENTAGE" ? (
                        <NumberInput
                          w={"60%"}
                          max={100}
                          disabled={!values.discountType}
                          value={values?.discountValue ?? ""}
                          onChange={(v) => setFieldValue("discountValue", v)}
                          label=""
                          placeholder="0.00"
                          hideControls
                          leftSection={<PercentIcon size={18} stroke="1.5" color="gray" opacity={0.8} />}
                        />
                      ) : (
                        <NumberCurrencyField
                          w={"60%"}
                          max={1000000000}
                          disabled={!values.discountType}
                          value={values.discountValue}
                          onChange={(v) => setFieldValue("discountValue", v)}
                          error={errors?.discountValue}
                          label=""
                          placeholder="0.00"
                          required
                        />
                      )}
                      <Select
                        label=""
                        value={values?.discountType ?? null}
                        onChange={(v) => {
                          setFieldValue("discountType", v)
                          setFieldValue("discountValue", undefined)
                        }}
                        data={[
                          { label: t("supplierSales:sales.percentage"), value: "PERCENTAGE" },
                          { label: t("supplierSales:sales.amount"), value: "AMOUNT" },
                        ]}
                        placeholder={t("common:actions.select")}
                        clearable
                      />
                    </Flex>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Paper>

            {/* 3. Хавсралт нэмэх — the table appears once a row exists */}
            <Paper className={classes.card} p={20}>
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <Box>
                  <Text className={classes.cardTitle}>
                    {t("supplierSales:sales.addAttachment")} <Text span className={classes.count}>· {attachments.length}</Text><HelpIcon helpKey="order.supplier-sales-new.section.attachment" variant="section" />
                  </Text>
                  {attachments.length === 0 && <Text className={classes.muted}>{t("supplierSales:sales.noAttachments")}</Text>}
                </Box>
                <Button variant="default" leftSection={<PlusIcon size={16} />} onClick={addAttachment}>
                  {t("supplierSales:sales.addAttachment")}
                </Button>
              </Group>
              {attachments.length > 0 && (
                <Box className={classes.plainTable} mt="md">
                  <Table columns={columnsAttachments} name="values.attachments" dataSource={values.attachments} pagination={false} />
                </Box>
              )}
            </Paper>
          </Stack>
        </Grid.Col>

        {/* 4. Захиалгын нэгтгэл */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper className={classes.card} p={20}>
            <Text className={classes.cardTitle}>{t("supplierSales:sales.orderSummary")}<HelpIcon helpKey="order.supplier-sales-new.section.summary" variant="section" /></Text>
            <Divider my="md" color="gray.3" />
            <Stack gap={0}>
              {summaryRows.map((row, i) => (
                <Group key={i} justify="space-between" wrap="nowrap" gap="md" className={classes.sumRow}>
                  <Text className={classes.sumLabel}>{row.label}</Text>
                  <Text ff="monospace" className={classes.sumValue}>
                    {row.value}
                  </Text>
                </Group>
              ))}
            </Stack>
            <Divider my="md" color="gray.3" />
            <Group justify="space-between" wrap="nowrap" gap="md">
              <Text className={classes.grandLabel}>{t("supplierSales:sales.grandTotal")}</Text>
              <Text ff="monospace" className={classes.grandValue}>
                {currencyFormat(summaryLines.length ? grandTotal : 0)}
              </Text>
            </Group>
            {isCod && (
              <Stack gap={0} mt="sm">
                <Group justify="space-between" wrap="nowrap" gap="md" className={classes.sumRow}>
                  <Text className={classes.sumLabel}>{t("supplierSales:sales.paymentConfirm")}</Text>
                  <Text ff="monospace" className={classes.sumValue}>
                    {!values?.lines || !values?.paymentTerm ? currencyFormat(0) : currencyFormat(codAdvance)}
                  </Text>
                </Group>
                <Group justify="space-between" wrap="nowrap" gap="md" className={classes.sumRow}>
                  <Text className={classes.sumLabel}>{t("supplierSales:sales.paymentConfirmDate")}</Text>
                  <Text ff="monospace" className={classes.sumValue}>
                    {dateTimeSecFormat(now_date.setDate(now_date.getDate() + values?.paymentTerm?.confirmationDay))}
                  </Text>
                </Group>
              </Stack>
            )}
            <Text className={classes.sumLabel} mt="md">
              {t("supplierSales:sales.paymentTerm")}
            </Text>
            <Text className={classes.termText}>{values?.paymentTerm?.description || "-"}</Text>
          </Paper>
        </Grid.Col>
      </Grid>

      <VariantSelectorDrawer
        opened={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        businessId={values?.supplierId || values?.receiverBusinessId}
        sellerBusinessId={values?.senderBusinessId}
        buyerBusinessId={values?.receiverBusinessId}
        orderType="SALES"
        selectedKeys={(values?.lines || []).map((l: any) => `${l.variantId}-${l.warehouseId}`)}
        onAdd={(line) => setFieldValue("lines", [...(values?.lines || []), line])}
        onAddAll={(lines) => setFieldValue("lines", [...(values?.lines || []), ...lines])}
      />
      <Modal
        opened={action[0] === "add" && values?.supplierId}
        onClose={() => {
          setAction([])
        }}
        withCloseButton={false}
        size="900px"
        padding={0}
        centered>
        <SupplierSalesDynamicAdditionalForm values={values} action={action} setFieldValue={setFieldValue} setAction={setAction} />
      </Modal>
    </Stack>
  )
}

// Lines table of Ord_SalesEdit. Fixed widths for everything but the product
// cell, so the table fits the card without a horizontal scrollbar (FitTable).
const useHeader = ({
  t,
  onClick,
  reload,
  values,
  setFieldValue,
  errors,
}: {
  t: any
  reload: boolean
  onClick: (e: any, e1: any) => void
  values: any
  setFieldValue: any
  errors: any
}): ColumnType<any>[] => [
    {
      title: t("common:actions.action"),
      width: 100,
      render: (record, index) => (
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => onClick("remove", { ...record, index })}
          disabled={reload}
          aria-label={t("common:actions.delete")}>
          <TrashIcon size={18} />
        </ActionIcon>
      ),
    },
    {
      title: t("supplierSales:sales.productService"),
      dataIndex: "nameApp",
      render: (record) => (
        <Group gap="sm" wrap="nowrap">
          <Avatar size={40} src={record?.image || null} radius="sm">
            <ImageIcon />
          </Avatar>
          <Box miw={0}>
            <Text className={classes.productName} lineClamp={2}>
              {`${record.nameMon || record.name || ""}${record.optionValue ? " - " + record.optionValue : ""}`}
            </Text>
            <Text ff="monospace" className={classes.sku}>
              {record?.skuCode || "-"}
            </Text>
          </Box>
        </Group>
      ),
    },
    {
      title: t("supplierSales:sales.warehouse"),
      dataIndex: "warehouseId",
      width: 220,
      render: (record, index) => {
        const options =
          record.inventories?.map((inv: any) => ({
            value: inv.warehouseId,
            label: inv.warehouseName,
          })) ?? []

        const selectedWarehouse = values.lines[index]?.warehouseId
        if (!selectedWarehouse && options.length === 1) {
          setFieldValue(`lines.${index}.warehouseId`, options[0].value)
        }

        return (
          <Select
            value={values?.lines?.[index]?.warehouseId ?? null}
            onChange={(v) => setFieldValue(`lines.${index}.warehouseId`, v)}
            placeholder={t("supplierSales:sales.selectWarehouse")}
            data={options}
            clearable
          />
        )
      },
    },
    {
      title: t("supplierSales:sales.unitPrice"),
      dataIndex: "standardPrice",
      width: 140,
      align: "right",
      titleAlign: "right",
      render: (record, index) => (
        <Box>
          {record.isEdit ? (
            <NumberCurrencyField
              value={values?.lines?.[index]?.price}
              onChange={(v) => setFieldValue(`lines.${index}.price`, v)}
              error={errors?.[`lines.${index}.price`]}
              placeholder="0.00"
            />
          ) : (
            <Text ff="monospace" className={classes.amount}>
              {currencyFormat(record?.price || 0)}
            </Text>
          )}
          {record?.priceGroupName ? (
            <Text className={classes.sku}>
              {/* Stored as a code (PROMOTION / SPECIAL / SEGMENT / BASE / FREE_GOODS); show its name. */}
              {t(`order:buyerOrders.detail.priceSrc.${PRICE_SRC_KEY[String(record.priceGroupName).toUpperCase()] ?? "base"}`)}
            </Text>
          ) : null}
        </Box>
      ),
    },
    {
      title: t("supplierSales:sales.orderQuantity"),
      dataIndex: "quantity",
      width: 150,
      render: (record, index) => {
        const qty = Number(values?.lines?.[index]?.quantity) || 0
        // Same guard the previous quantity editor applied: ignore empty, zero and negative.
        const setQuantity = (val: number | string) => {
          const numVal = typeof val === "number" ? val : Number(val)
          if (!numVal || numVal < 0) return
          setFieldValue(`lines.${index}.quantity`, numVal)
        }
        return (
          <Group gap={0} wrap="nowrap" className={classes.stepper}>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setQuantity(qty - 1)}
              disabled={qty <= 1}
              aria-label="-">
              <MinusIcon size={14} />
            </ActionIcon>
            <NumberInput
              max={9000000}
              min={0}
              hideControls
              variant="unstyled"
              flex={1}
              miw={0}
              classNames={{ input: classes.stepperInput }}
              value={values?.lines?.[index]?.quantity ?? ""}
              onChange={setQuantity}
              placeholder={t("supplierSales:sales.quantity")}
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setQuantity(Math.min(qty + 1, 9000000))}
              aria-label="+">
              <PlusIcon size={14} />
            </ActionIcon>
          </Group>
        )
      },
    },
    {
      title: t("supplierSales:sales.vat"),
      dataIndex: "hasVat",
      width: 120,
      align: "right",
      titleAlign: "right",
      render: (record) => (
        <Text ff="monospace" className={classes.amount}>
          {currencyFormat(record?.vatAmount)}
        </Text>
      ),
    },
    {
      title: t("supplierSales:sales.tax"),
      dataIndex: "hasTax",
      width: 90,
      align: "right",
      titleAlign: "right",
      render: (record) => (
        <Text ff="monospace" className={classes.amount}>
          {currencyFormat(record?.taxAmount)}
        </Text>
      ),
    },
    {
      title: t("supplierSales:sales.totalAmount"),
      dataIndex: "name",
      width: 150,
      align: "right",
      titleAlign: "right",
      render: (record: any) => {
        // DSC-001: was `price * quantity + vatAmount`, i.e. one unit of an
        // already-included VAT added on top of the whole line, and the line's own
        // discount ignored. Computed live rather than read from the stored
        // `totalAmount` — this table is editable, so price and quantity move.
        const count = lineNetAmount(record)
        // The line discount had its own column; it is display-only, so it now
        // sits under the total when there is one (same expression as before).
        const discount =
          record?.discountType === "PERCENTAGE"
            ? ((record?.price || 0) * (record?.quantity || 0) * record.discountValue) / 100
            : (record?.quantity || 0) * record?.discountValue
        return (
          <Box>
            <Text ff="monospace" className={classes.total}>
              {currencyFormat(count || 0)}
            </Text>
            {discount > 0 ? (
              <Text className={classes.sku}>
                {t("supplierSales:sales.discount")} -{currencyFormat(discount)}
              </Text>
            ) : null}
          </Box>
        )
      },
    },
  ]

const useHeaderAdditional = ({
  t,
  edit,
  setEdit,
  onClick,
  setFieldValue,
  values,
  errors,
}: {
  t: any
  edit: any
  setEdit: any
  setFieldValue: any
  onClick: (e: any, e1: any) => void
  values: any
  errors: any
}): ColumnType<IAdditionalLine & { isEdit: boolean }>[] => [
    {
      title: "#",
      render: (_r, index) => `${index + 1}`,
    },
    {
      title: t("supplierSales:sales.itemName"),
      dataIndex: "name",
      render: (record, index) => {
        if (record.isEdit) return (
          <TextInput
            w={260}
            size="sm"
            value={values?.additionalLines?.[index]?.name ?? ""}
            onChange={(e) => setFieldValue(`additionalLines.${index}.name`, e.target.value)}
            placeholder={t("supplierSales:sales.name")}
          />
        )
        else
          return (
            <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
              {record?.name || "-"}
            </Text>
          )
      },
    },
    {
      title: t("supplierSales:sales.unit"),
      dataIndex: "unit.name",
      render: (record, index) => {
        if (record.isEdit) return (
          <TextInput
            w={170}
            size="sm"
            value={values?.additionalLines?.[index]?.unit ?? ""}
            onChange={(e) => setFieldValue(`additionalLines.${index}.unit`, e.target.value)}
            placeholder={t("supplierSales:sales.unit")}
          />
        )
        else
          return (
            <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
              {record?.unit || "-"}
            </Text>
          )
      },
    },
    {
      title: t("supplierSales:sales.unitPrice"),
      dataIndex: "standardPrice",
      titleAlign: "right",
      render: (record, index) => {
        if (record.isEdit) return (
          <NumberCurrencyField
            w={150}
            value={values?.lines?.[index]?.price}
            onChange={(v) => setFieldValue(`lines.${index}.price`, v)}
            error={errors?.[`lines.${index}.price`]}
            placeholder="0.00"
          />
        )
        else return <RenderText text={currencyFormat(record?.price || 0)} isAmount />
      },
    },
    {
      title: t("supplierSales:sales.quantity"),
      dataIndex: "quantity",
      render: (record, index) => {
        if (record.isEdit) return (
          <NumberInput
            w={110}
            max={9000000}
            min={0}
            hideControls
            size="sm"
            value={values?.additionalLines?.[index]?.quantity ?? ""}
            onChange={(v) => setFieldValue(`additionalLines.${index}.quantity`, v)}
            placeholder="0"
          />
        )
        else
          return (
            <Group align="apart">
              {edit === index ? (
                <NumberInput
                  w={110}
                  max={9000000}
                  min={0}
                  hideControls
                  size="sm"
                  value={values?.additionalLines?.[index]?.quantity ?? ""}
                  onChange={(v) => setFieldValue(`additionalLines.${index}.quantity`, v)}
                  placeholder={`${t("supplierSales:sales.quantity")}:`}
                />
              ) : (
                <Text w={150}>
                  <Badge color="brand" size="md" radius="sm" variant="outline">
                    {record.quantity || "0"}
                  </Badge>
                </Text>
              )}
              <ActionIcon onClick={() => setEdit(edit !== index ? index : null)}>{edit === index ? <FloppyDiskIcon /> : <PencilSimpleIcon />}</ActionIcon>
            </Group>
          )
      },
    },
    {
      title: t("supplierSales:sales.discountType"),
      dataIndex: "discountType",
      render: (record, index) => {
        if (record.isEdit)
          return (
            <Select
              w={160}
              size="sm"
              label=""
              value={values?.additionalLines?.[index]?.discountType ?? null}
              onChange={(v) => {
                setFieldValue(`additionalLines.${index}.discountType`, v)
                setFieldValue(`additionalLines.${index}.discountValue`, undefined)
              }}
              data={[
                { label: t("supplierSales:sales.percentage"), value: "PERCENTAGE" },
                { label: t("supplierSales:sales.amount"), value: "AMOUNT" },
              ]}
              placeholder={t("common:actions.select")}
              clearable
            />
          )
        else
          return (
            <Group align="apart">
              {edit === index ? (
                <Select
                  w={160}
                  label=""
                  size="sm"
                  value={values?.additionalLines?.[index]?.discountType ?? null}
                  onChange={(v) => {
                    setFieldValue(`additionalLines.${index}.discountType`, v)
                    setFieldValue(`additionalLines.${index}.discountValue`, undefined)
                  }}
                  data={[
                    { label: t("supplierSales:sales.percentage"), value: "PERCENTAGE" },
                    { label: t("supplierSales:sales.amount"), value: "AMOUNT" },
                  ]}
                  placeholder={t("common:actions.select")}
                  clearable
                />
              ) : (
                <Text w={150}>
                  <Badge color="brand" size="md" radius="sm" variant="outline">
                    {record.discountType === "PERCENTAGE" && t("supplierSales:sales.percentage")}
                    {record.discountType === "AMOUNT" && t("supplierSales:sales.amount")}
                    {!record?.discountType && "-"}
                  </Badge>
                </Text>
              )}
            </Group>
          )
      },
    },
    {
      title: t("supplierSales:sales.discount"),
      dataIndex: "discountType",
      render: (record, index) => {
        return (
          <>
            {record?.discountType === "AMOUNT" && (
              <NumberCurrencyField
                w={150}
                max={1000000000}
                size="sm"
                label=""
                placeholder="0.00"
                required
                value={values?.additionalLines?.[index]?.discountValue}
                onChange={(v) => setFieldValue(`additionalLines.${index}.discountValue`, v)}
                error={errors?.[`additionalLines.${index}.discountValue`]}
              />
            )}
            {record?.discountType === "PERCENTAGE" && (
              <NumberInput
                w={150}
                max={100}
                size="sm"
                value={values?.additionalLines?.[index]?.discountValue ?? ""}
                onChange={(v) => setFieldValue(`additionalLines.${index}.discountValue`, v)}
                label=""
                placeholder="0.00"
                hideControls
                leftSection={<PercentIcon size={18} stroke="1.5" color="gray" opacity={0.8} />}
              />
            )}
            {!record?.discountType && (
              <NumberCurrencyField
                w={150}
                max={1000000000}
                size="sm"
                disabled
                label=""
                placeholder="0.00"
                required
                value={values?.additionalLines?.[index]?.discountValue}
                onChange={(v) => setFieldValue(`additionalLines.${index}.discountValue`, v)}
                error={errors?.[`additionalLines.${index}.discountValue`]}
              />
            )}
          </>
        )
      },
    },
    {
      title: t("supplierSales:sales.totalPrice"),
      dataIndex: "name",
      render: (record: any) => {
        let count
        count =
          (record?.price || 0) * (record?.quantity || 0) -
          (record?.discountType === "PERCENTAGE" ? ((record?.price || 0) * (record?.quantity || 0) * record.discountValue) / 100 : record?.discountValue)
        return (
          <Group ta="right">
            <Text size="sm" fw={500} c="var(--mantine-color-dark-5)" w={"max-content"}>
              {currencyFormat(count || 0)}
            </Text>
          </Group>
        )
      },
    },
    {
      title: t("common:actions.action"),
      align: "right",
      render: (record, index) => {
        return (
          <Flex gap={8} align="center" w={"max-content"}>
            <ActionIcon variant="subtle" color="gray" onClick={() => onClick("remove", { ...record, index })} aria-label={t("common:actions.delete")}>
              <TrashIcon size={18} />
            </ActionIcon>
          </Flex>
        )
      },
    },
  ]

const useHeaderAttachments = ({
  t,
  onClick,
  setFieldValue,
  errors,
  values,
}: {
  t: any
  setFieldValue: any
  onClick: (e: any, e1: any) => void
  errors: any
  values: any
}): ColumnType<IAttachment & { isEdit: boolean }>[] => [
    {
      title: "#",
      render: (_r, index) => `${index + 1}`,
    },
    {
      title: t("supplierSales:sales.name"),
      dataIndex: "name",
      render: (record, index) => {
        if (record.isEdit) {
          return (
            <TextInput
              w={320}
              size="sm"
              value={values?.attachments?.[index]?.name ?? ""}
              onChange={(e) => setFieldValue(`attachments.${index}.name`, e.target.value)}
              placeholder={`${t("supplierSales:sales.name")}:`}
            />
          )
        } else
          return (
            <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
              {record?.name || "-"}
            </Text>
          )
      },
    },
    {
      title: t("supplierSales:sales.fileName"),
      dataIndex: "url",
      render: (record, index) => {
        if (record.isEdit) {
          return (
            <TextInput
              w={320}
              size="sm"
              value={values?.attachments?.[index]?.url ?? ""}
              onChange={(e) => setFieldValue(`attachments.${index}.url`, e.target.value)}
              placeholder={t("supplierSales:sales.url")}
            />
          )
        } else
          return (
            <Text size="sm" fw={500} c="var(--mantine-color-dark-5)" w="max-content">
              {record?.url ? (
                <a href={record.url} download>
                  {record.url.split("/").pop()}
                </a>
              ) : (
                "-"
              )}
            </Text>
          )
      },
    },
    {
      title: t("supplierSales:sales.description"),
      dataIndex: "description",
      render: (record, index) => {
        if (record.isEdit) {
          return (
            <Textarea
              value={values?.attachments?.[index]?.description ?? ""}
              onChange={(e) => setFieldValue(`attachments.${index}.description`, e.target.value)}
              placeholder={`${t("supplierSales:sales.description")}:`}
              maxLength={255}
            />
          )
        } else
          return (
            <Text size="sm" fw={500} c="var(--mantine-color-dark-5)">
              {record?.description || "-"}
            </Text>
          )
      },
    },
    {
      title: t("common:actions.action"),
      align: "right",
      render: (record, index) => {
        return (
          <Flex gap={8} align="center" w={"max-content"}>
            <ActionIcon variant="subtle" color="gray" onClick={() => onClick("remove", { ...record, index })} aria-label={t("common:actions.delete")}>
              <TrashIcon size={18} />
            </ActionIcon>
          </Flex>
        )
      },
    },
  ]
